import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import get from 'lodash/get'
import styled, { useTheme } from 'styled-components'
import { size } from 'styled-theme'
import { Box, Flex } from '@rebass/grid'
import { LuImagePlus } from 'react-icons/lu'
import Text from '../../atoms/Text'
import Button from '../../atoms/Button'
import Viewport from '../../atoms/Viewport'
import ContentBox from '../../atoms/ContentBox'
import Modal from '../../molecules/Modal'
import FileDropzone from '../../molecules/FileDropzone'
import AdSenseGroupView from '../../molecules/AdSenseGroupView'
import { ERROR_QUOTA_EXCEEDED } from '../../molecules/MessageSender'
import Chatroom from '../../organisms/Chatroom'
import TopUpPayment from '../../organisms/TopUpPayment'
import { handleErrorResponse } from '../../../utlils/general'
import { getMyUserState, updateMyUserState } from '../../../api/users'
import { restartChat } from '../../../api/messages'
import { SOCKET_EVENT_MESSAGE_QUOTA_UPDATE, SOCKET_EVENT_USER_STATE_UPDATE } from '../../../socket/socket-io'
import useChatMessages from '../../../hooks/useChatMessages'
import { useLoading } from '../../../contexts/loading'
import { useAuth } from '../../../contexts/auth'
import UserDecisionActionSection from './UserDecisionAction'
import FindNextActionSection, { FIND_NEXT_ACTION_QUOTA } from './FindNextAction'

const BOT_USER_ID = '00000000-0000-0000-0000-000000000000'

const LOCAL_STORAGE_KEY_NO_USER_TOOLTIP_OPENED = 'NO_USER_TOOLTIP_OPENED'
const LOCAL_STORAGE_KEY_NO_EVENT_TOOLTIP_OPENED = 'NO_EVENT_TOOLTIP_OPENED'
const LOCAL_STORAGE_KEY_CHANGE_REQUIREMENT_TOOLTIP_OPENED = 'CHANGE_REQUIREMENT_TOOLTIP_OPENED'

const getResetTooltipState = (userState, username) => {
  if (userState?.mode === 'events') {
    if (userState?.step === 2) {
      const localStorageKey = `${username}_${LOCAL_STORAGE_KEY_NO_EVENT_TOOLTIP_OPENED}`
      return {
        open: !localStorage.getItem(localStorageKey),
        message: '想轉下要求！？\n咁就要重新再黎過！',
        localStorageKey,
      }
    }
    return null
  }
  if (userState?.step === 3) {
    const localStorageKey = `${username}_${LOCAL_STORAGE_KEY_CHANGE_REQUIREMENT_TOOLTIP_OPENED}`
    return {
      open: !localStorage.getItem(localStorageKey),
      message: '想轉下要求！？\n咁就要重新再黎過！',
      localStorageKey,
    }
  }
  if (userState?.step === 2 && !userState?.suggested_user_id) {
    const localStorageKey = `${username}_${LOCAL_STORAGE_KEY_NO_USER_TOOLTIP_OPENED}`
    return {
      open: !localStorage.getItem(localStorageKey),
      message: '暫時冇人啱，\n不如...試下重新再黎過？',
      localStorageKey,
    }
  }
  return null
}

const StyledViewport = styled(Viewport)`
  padding: 4.5rem 1rem 3rem;
  @media only screen and (max-width: ${size('mobile')}) {
    padding: 4rem 0 0;
  }
`

const StyledContentBox = styled(ContentBox)`
  height: 100%;
  padding: 0.5rem;
`

const EmailVerificationReminder = () => (
  <Flex
    flex={1}
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    marginTop="4rem"
    marginBottom="2rem"
  >
    <Text variant="h2" align="center" palette="error">⚠️ 注意 ⚠️</Text>
    <Flex flexDirection="column" alignItems="center" marginTop="1rem" marginBottom="1rem">
      <Text variant="h3" palette="primary" align="center">請先驗證<b>E-mail</b> ☝️</Text>
      <Text variant="h3" palette="primary" align="center">以繼續與Crush仔對話</Text>
    </Flex>
    <Flex alignItems="center">
      <Button variant="text" padding={0} palette="grayscale" to="/profile">個人頁面</Button>
      <Text style={{ marginLeft: 4, marginRight: 4 }}> ➡️ E-mail</Text>
      <Text> ➡️ 驗證</Text>
    </Flex>
  </Flex>
)

const HomePage = () => {
  const [userState, setUserState] = useState({})
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [confirmingNewMode, setConfirmingNewMode] = useState(null)
  const [confirmingReset, setConfirmingReset] = useState(false)
  const [topUpModalOpen, setTopUpModalOpen] = useState(false)
  const [resetTooltipOpen, setResetTooltipOpen] = useState(false)
  const [waitingBot, setWaitingBot] = useState(false)
  const { auth, setAuth, setAuthUser } = useAuth()
  const { setLoading } = useLoading()
  const chatroomRef = useRef(null)
  const theme = useTheme()

  const username = get(auth.user, 'username')

  const { chat: currentChat, messages, webSocket, sendChatMessage, fetchMessages } = useChatMessages(
    useMemo(() => ({
      bot_reply: 1,
      orderByDesc: 'createdAt',
    }), []),
  )

  const messageRemaining = useMemo(
    () => get(auth.user, 'message_remaining') || 0,
    [auth.user],
  )

  const isBotTyping = useMemo(() => {
    if (messages.length === 0) {
      return false
    }
    const lastMessage = messages[messages.length - 1]
    return lastMessage.content === '正在輸入中...'
  }, [messages])

  const resetTooltipState = useMemo(
    () => getResetTooltipState(userState, username),
    [userState, username],
  )

  const fetchUserStateFromApi = useCallback(() => {
    const userStateParams = {}
    return getMyUserState(userStateParams)
      .then(({ data }) => {
        setUserState(data)
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth)
      })
  }, [setAuth])

  const handleSendMessage = useCallback((message, replyId, appendImages) => {
    if (messageRemaining <= 0) {
      setTopUpModalOpen(true)
      throw new Error(ERROR_QUOTA_EXCEEDED)
    }
    setWaitingBot(true)
    return sendChatMessage(message, replyId, appendImages)
  }, [messageRemaining, sendChatMessage])

  const onChangeUserModeToApi = useCallback((newMode) => {
    if (!currentChat) {
      return Promise.resolve()
    }
    let oriMode = null
    setUserState((oriState) => {
      oriMode = oriState.mode
      return { ...oriState, mode: newMode }
    })
    setLoading(true)
    return updateMyUserState({ mode: newMode, chat_id: currentChat?.chat_id })
      .then(() => fetchMessages())
      .catch((err) => {
        setUserState((oriState) => ({ ...oriState, mode: oriMode }))
        handleErrorResponse(err, setAuth)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [currentChat, fetchMessages, setLoading, setAuth])

  const onResetChatToApi = useCallback(() => {
    if (!currentChat) {
      return Promise.resolve()
    }
    setLoading(true)
    return restartChat(currentChat.chat_id, {})
      .then(() => fetchMessages())
      .catch((err) => {
        handleErrorResponse(err, setAuth)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [currentChat, fetchMessages, setLoading, setAuth])

  const onCloseResetTooltip = useCallback(() => {
    setResetTooltipOpen(false)
    if (resetTooltipState?.localStorageKey) {
      localStorage.setItem(resetTooltipState.localStorageKey, true)
    }
  }, [resetTooltipState])

  useEffect(() => {
    if (resetTooltipState?.open) {
      setResetTooltipOpen(true)
    }
  }, [resetTooltipState])

  useEffect(() => {
    if (currentChat) {
      fetchUserStateFromApi()
    }
  }, [currentChat, fetchUserStateFromApi])

  useEffect(() => {
    if (webSocket && currentChat && auth.user) {
      webSocket.on(SOCKET_EVENT_USER_STATE_UPDATE, (updatedState) => {
        setUserState(updatedState)
      })

      webSocket.on(SOCKET_EVENT_MESSAGE_QUOTA_UPDATE, (messageQuotaUpdate) => {
        setAuthUser((oriUser) => ({
          ...oriUser,
          ...messageQuotaUpdate,
        }))
      })
    }
  }, [auth.user, currentChat, webSocket, setAuthUser])

  const hasAcceptRejectSection = useMemo(() => userState?.step === 2 && !!userState?.suggested_user_id, [userState])
  const hasFindNextSection = useMemo(() => userState?.step === 3 && !!userState?.chosen_user_id, [userState])
  const imageSendable = useMemo(() => userState?.mode === 'users' && userState?.step === 1, [userState])
  const hasImageUploadSection = useMemo(() => imageSendable && messages.length <= 1, [imageSendable, messages.length])

  const handleChatroomPickImages = React.useCallback((files) => {
    return chatroomRef.current?.onPickImages?.(files)
  }, [])

  const customSection = useMemo(() => {
    if (!get(auth.user, 'email_verified')) {
      return (
        <EmailVerificationReminder />
      )
    }
    if (isBotTyping) {
      return null
    }
    const actionDisabled = !currentChat
      || !get(auth.user, 'active')
      || !get(auth.user, 'email_verified')
      || waitingBot

    const handleSendAction = (message) => {
      try {
        return handleSendMessage(message)
      } catch (err) {
        if (err.message === ERROR_QUOTA_EXCEEDED) {
          return
        }
        alert('系統有啲野出錯左！\n請等陣再試>.<')
      }
    }

    if (hasAcceptRejectSection) {
      return (
        <UserDecisionActionSection
          onSendMessage={handleSendAction}
          disabled={actionDisabled}
        />
      )
    }
    if (hasFindNextSection) {
      return (
        <FindNextActionSection
          disabled={actionDisabled}
          username={get(auth.user, 'username')}
          onSendMessage={(
            messageRemaining < FIND_NEXT_ACTION_QUOTA
              ? () => setTopUpModalOpen(true)
              : handleSendAction
          )}
        />
      )
    }
    if (hasImageUploadSection) {
      return (
        <FileDropzone
          onAdd={handleChatroomPickImages}
          accept={{
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
          }}
        >
          <LuImagePlus
            size={60}
            color={theme.palette.primary[0]}
            style={{ marginBottom: '0.5rem' }}
          />
          <Text variant="h4" align="center" style={{ marginBottom: '1rem' }}>
            你可以直接Upload理想型嘅圖上黎<br />Crush仔🤖就會跟住張相幫你搵架啦！
          </Text>
          <Text variant="body2" align="center" palette="grayscale" paletteIndex={1}>
            拉張圖過黎<br />或者撳落黎都得！
          </Text>
        </FileDropzone>
      )
    }
    return null
  }, [auth.user, messageRemaining, currentChat, isBotTyping, waitingBot, hasAcceptRejectSection, hasFindNextSection, hasImageUploadSection, handleSendMessage, handleChatroomPickImages, theme.palette])

  useEffect(() => {
    const lastMessage = messages[messages.length - 1]
    const isBotLastMessage = lastMessage?.sender_id === BOT_USER_ID
    if (isBotLastMessage && !isBotTyping) {
      setWaitingBot(false)
    }
  }, [isBotTyping, messages])

  useEffect(() => {
    if (confirmingNewMode || confirmingReset) {
      setConfirmModalOpen(true)
    }
  }, [confirmingNewMode, confirmingReset])

  useEffect(() => {
    if (!confirmModalOpen) {
      setTimeout(() => {
        setConfirmingReset(false)
        setConfirmingNewMode(null)
      }, 200)
    }
  }, [confirmModalOpen])

  return (
    <StyledViewport>
      <StyledContentBox>
        <Chatroom
          flex={1}
          style={{ minHeight: 0 }}
          userId={get(auth.user, 'user_id')}
          username={username}
          mode={userState?.mode}
          onChangeMode={setConfirmingNewMode}
          messages={!hasImageUploadSection ? messages : []}
          messageQuota={get(auth.user, 'message_quota') || 0}
          messageRemaining={messageRemaining}
          readOnly={!currentChat || isBotTyping || waitingBot || !get(auth.user, 'active') || !get(auth.user, 'email_verified')}
          inputDisabled={hasAcceptRejectSection}
          onMessageSend={handleSendMessage}
          onReset={() => setConfirmingReset(true)}
          onTopUp={() => setTopUpModalOpen(true)}
          resettable
          customSection={customSection}
          chatroomRef={chatroomRef}
          imageSendable={imageSendable}
          resetRecommended={!!resetTooltipState?.message}
          resetTooltipOpen={resetTooltipOpen}
          onCloseResetTooltip={onCloseResetTooltip}
          resetTooltipContent={(
            <Text palette="white">{resetTooltipState?.message}</Text>
          )}
        />
      </StyledContentBox>
      <Modal
        showCloseBtn
        isOpen={topUpModalOpen}
        onClose={() => setTopUpModalOpen(false)}
      >
        <Flex mb="1rem">
          <TopUpPayment
            message={'想同Crush仔傾多啲？\n你可以根據個人需要選擇你想要增值嘅用量！'}
            onComplete={() => setTopUpModalOpen(false)}
          />
        </Flex>
      </Modal>
      <Modal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        showCloseBtn
      >
        <Flex flexDirection="column" alignItems="center" p="1rem">
          {confirmingReset && (
            <Text variant="h3" align="center">{'同Crush仔🤖\n嘅對話紀錄會被清除\n確定繼續？'}</Text>
          )}
          {!!confirmingNewMode && (
            <Text variant="h3" align="center">{'更改模式會重設對話\n確定繼續？'}</Text>
          )}
          <Flex mt="2rem" width={1}>
            <Box flex={1}>
              <Button
                fullWidth
                variant="outline"
                palette="error"
                onClick={() => setConfirmModalOpen(false)}
              >
                取消
              </Button>
            </Box>
            <Box flex={1} ml="1rem">
              <Button
                onClick={() => {
                  if (confirmingReset) {
                    onResetChatToApi()
                  }
                  if (confirmingNewMode) {
                    onChangeUserModeToApi(confirmingNewMode)
                  }
                  setConfirmModalOpen(false)
                }}
                fullWidth
              >
                確定
              </Button>
            </Box>
          </Flex>
        </Flex>
      </Modal>
      <AdSenseGroupView />
    </StyledViewport>
  )
}

export default HomePage
